// ViewSwitcher (spec 16 §4, `views-switcher`). It sits in the list's filter
// row: a wrapping group of pressed chips in the order All, the system views,
// the reader's own views, then the Shared group (other members' shared views,
// a nested group labelled "Shared" with `data-group="shared"`), then More
// views when the reader holds more than six own and shared views. Beside the
// chips it shows the Modified marker and the notices, and its controls are
// Columns, Save (the author only), Save as new, Reset and Manage.
//
// WIDTHS (ruling 20). Applying a view, clearing it and Reset work at every
// width, and More views opens the apply-only list at every width. Save, Save
// as new, Manage and Columns are absent below 769 px: a phone renders no
// widths and orders its cards by the configuration, so authoring there would
// store a layout its author cannot see.
//
// STATES. Loading reserves one chip line and holds no control (the screen
// reveals this row and the table together, `ViewsRegion`, so it is a
// placeholder the reveal covers). Empty shows All and "No saved views yet",
// with Save as new and Columns beside it from 769 px up. When the views read
// fails the row shows one sentence with Retry at the height of one chip line,
// and nothing else, and says so when the address named a view that could
// therefore not be applied (W5, PR-E4 wave 5). A Retry in flight keeps that
// row, with the Retry button busy, until the read answers (W4). A read paused
// offline is a failed read, and its sentence says the reader is offline (R2,
// PR-E4 wave 4). A refusal of the read (403) hides the row, controls
// included, since the screen gate is the list's own read and the list states
// the refusal for the same reader (R4). It stays hidden until a read
// succeeds, through a re-read on window focus (W4), one paused offline and
// one that fails another way (V3, PR-E4 wave 6). Retry pressed offline
// answers at once with the offline sentence and is not left busy. A fenced
// item is never named: the fence drops it in silence (item 7).
//
// RESET (R1). Reset shows at every width, the Empty row included, whenever
// pressing it would change the address or the columns (`views.canReset`), and
// never otherwise. The controller offers it only once the views read has
// answered and any arrival has applied, so the Loading and Error rows never
// hold it (W1, W5).

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { ColumnChooser } from "../../../../components/column-chooser";
import { Skeleton } from "../../../../components/states";
import { ViewChips, type ViewChip } from "../../../../components/view-chips";
import { RequestError, requestIdOf } from "../../../../lib/request";
import type { SavedViewsController } from "../hooks/use-saved-views";
import type { ViewError } from "../lib/view-errors";
import { viewLabel } from "../lib/view-label";
import type { SavedView } from "../lib/view-state";
import { WriteKeys } from "../lib/write-key";
import { ApplyViewsModal } from "./apply-views-modal";
import { ManageViewsModal } from "./manage-views-modal";
import { SaveViewModal } from "./save-view-modal";
import { ViewErrorLine } from "./view-error-line";
import "../views.css";

type Dialog = "none" | "save" | "manage" | "more" | "columns";

export interface ViewSwitcherProps<S extends object, Row, K extends string> {
  readonly views: SavedViewsController<S, Row, K>;
}

export function ViewSwitcher<S extends object, Row, K extends string>({
  views,
}: ViewSwitcherProps<S, Row, K>) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<Dialog>("none");
  const [saveError, setSaveError] = useState<ViewError | undefined>(undefined);
  // Save keeps its key per view until the server answers (V2).
  const [saveKeys] = useState(() => new WriteKeys());

  if (views.status === "pending") {
    return (
      <div
        className="ds-viewswitcher"
        role="status"
        aria-label={t("views.switcher.loading")}
        data-testid="views-switcher"
        data-state="loading"
      >
        <Skeleton width="3rem" height="26px" />
        <Skeleton width="6rem" height="26px" />
      </div>
    );
  }

  const resetButton = views.canReset ? (
    <Button
      size="sm"
      data-testid="views-reset"
      onClick={() => {
        setSaveError(undefined);
        views.reset();
      }}
    >
      {t("views.tools.reset")}
    </Button>
  ) : null;

  if (views.status === "error") {
    if (views.error instanceof RequestError && views.error.httpStatus === 403) return null;
    const requestId = requestIdOf(views.error);
    const sentence = views.offline
      ? views.namesView
        ? t("views.error.offlineNamed")
        : t("views.error.offline")
      : views.namesView
        ? t("views.error.readNamed")
        : t("views.error.read");
    return (
      <div className="ds-viewswitcher" data-testid="views-switcher" data-state="error">
        <span className="ds-viewswitcher-note views-error" role="alert">
          {sentence}
          {requestId !== undefined && requestId.length > 0 ? (
            <>
              {" "}
              <span className="views-request-id">
                {t("views.error.requestId")}: <code>{requestId}</code>
              </span>
            </>
          ) : null}
        </span>
        <Button
          size="sm"
          busy={views.retrying}
          busyLabel={t("views.error.retrying")}
          onClick={views.retry}
          data-testid="views-retry"
        >
          {t("views.error.retry")}
        </Button>
      </div>
    );
  }

  const { groups, row, desktop } = views;
  const active = views.activeView;
  const hasViews = groups.system.length + groups.own.length + groups.shared.length > 0;

  const chipOf = (view: SavedView): ViewChip => ({
    key: view.id,
    label: viewLabel(view, t),
    pressed: views.isPressed(view),
    onPress: () => {
      setSaveError(undefined);
      views.press(view);
    },
    testId: `views-chip-${view.id}`,
  });

  const chips: ViewChip[] = [
    {
      key: "all",
      label: t("views.switcher.all"),
      pressed: views.allPressed,
      onPress: () => {
        setSaveError(undefined);
        views.pressAll();
      },
      testId: "views-chip-all",
    },
    ...row.system.map(chipOf),
    ...row.own.map(chipOf),
  ];

  const columnsDisabled = views.rowsLoading || views.rows.length === 0;
  const canSave = desktop && active !== undefined && active.mutable && !active.is_system;

  const save = async () => {
    if (active === undefined) return;
    const write = saveKeys.for(active.id);
    setSaveError(undefined);
    try {
      await views.save(write.take());
      write.settle();
    } catch (caught) {
      setSaveError(write.settle(caught));
    }
  };

  return (
    <>
      <ViewChips
        label={t("views.switcher.label")}
        chips={chips}
        groups={[{ key: "shared", label: t("views.shared"), chips: row.shared.map(chipOf) }]}
        testId="views-switcher"
        trailing={
          row.more ? (
            <button
              type="button"
              className="ds-viewchip"
              aria-haspopup="dialog"
              data-testid="views-more"
              onClick={() => {
                setDialog("more");
              }}
            >
              {t("views.switcher.moreViews")}
            </button>
          ) : null
        }
      >
        {!hasViews ? (
          <span className="ds-viewswitcher-note" data-testid="views-empty">
            {t("views.switcher.empty")}
          </span>
        ) : null}
        {views.modified ? (
          <span className="ds-viewswitcher-note" data-testid="views-modified">
            {t("views.switcher.modified")}
          </span>
        ) : null}
        {views.notice !== undefined ? (
          <span className="ds-viewswitcher-note" role="status" data-testid="views-notice">
            {views.notice === "stale" ? t("views.notice.stale") : t("views.notice.gone")}
          </span>
        ) : null}
        <div className="ds-viewswitcher-tools">
          {desktop ? (
            <Button
              size="sm"
              disabled={columnsDisabled}
              title={columnsDisabled ? t("views.tools.columnsUnavailable") : undefined}
              aria-haspopup="dialog"
              data-testid="views-columns"
              onClick={() => {
                setDialog("columns");
              }}
            >
              {t("views.tools.columns")}
            </Button>
          ) : null}
          {canSave ? (
            <Button
              size="sm"
              busy={views.saving}
              busyLabel={t("views.tools.saving")}
              data-testid="views-save"
              onClick={() => {
                void save();
              }}
            >
              {t("views.tools.save")}
            </Button>
          ) : null}
          {desktop ? (
            <Button
              size="sm"
              aria-haspopup="dialog"
              data-testid="views-save-as-new"
              onClick={() => {
                setSaveError(undefined);
                setDialog("save");
              }}
            >
              {t("views.tools.saveAsNew")}
            </Button>
          ) : null}
          {resetButton}
          {desktop && hasViews ? (
            <Button
              size="sm"
              aria-haspopup="dialog"
              data-testid="views-manage"
              onClick={() => {
                setDialog("manage");
              }}
            >
              {t("views.tools.manage")}
            </Button>
          ) : null}
        </div>
        {saveError !== undefined ? (
          <ViewErrorLine
            error={saveError}
            testId="views-save-failed"
            filterLabel={views.filterLabel}
          />
        ) : null}
      </ViewChips>

      {dialog === "more" ? (
        <ApplyViewsModal
          groups={groups}
          activeId={active?.id}
          onApply={(view) => {
            setSaveError(undefined);
            views.apply(view);
          }}
          onClose={() => {
            setDialog("none");
          }}
        />
      ) : null}
      {dialog === "save" && desktop ? (
        <SaveViewModal
          onSubmit={views.saveAsNew}
          onClose={() => {
            setDialog("none");
          }}
          onOpenManage={() => {
            setDialog("manage");
          }}
          filterLabel={views.filterLabel}
        />
      ) : null}
      {dialog === "manage" && desktop ? (
        <ManageViewsModal
          groups={groups}
          rename={views.rename}
          setShared={views.setShared}
          remove={views.remove}
          onClose={() => {
            setDialog("none");
          }}
        />
      ) : null}
      {dialog === "columns" && desktop && !columnsDisabled ? (
        <ColumnChooser
          columns={views.columns}
          rows={views.rows}
          value={views.columnState}
          onChange={views.setColumnState}
          onClose={() => {
            setDialog("none");
          }}
          className="views-modal"
          testId="views-columns-modal"
          labels={{
            title: t("views.columns.title"),
            close: t("views.columns.close"),
            done: t("views.columns.done"),
            fixed: t("views.columns.fixed"),
            resetWidths: t("views.columns.resetWidths"),
            moveUp: (column) => t("views.columns.moveUp", { column }),
            moveDown: (column) => t("views.columns.moveDown", { column }),
          }}
        />
      ) : null}
    </>
  );
}
